import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BrainCircuit, CloudCog, Blocks, Smartphone, Zap, Shield, Database, Sparkles } from 'lucide-react';
import { staggerCards, fadeInOnScroll } from '../utils/animations';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    id: 1,
    icon: BrainCircuit,
    title: "AI & Machine Learning",
    description: "Custom neural networks, predictive models, and intelligent automation systems built with cutting-edge frameworks.",
    capabilities: [
      "Deep Learning Models",
      "Natural Language Processing",
      "Computer Vision",
      "Predictive Analytics"
    ]
  },
  {
    id: 2,
    icon: CloudCog,
    title: "Cloud Architecture",
    description: "Scalable, fault-tolerant infrastructure designed for high-performance distributed systems.",
    capabilities: [
      "Microservices Design",
      "Serverless Computing",
      "Auto-scaling Infrastructure",
      "Multi-cloud Deployment"
    ]
  },
  {
    id: 3,
    icon: Blocks,
    title: "Blockchain Development",
    description: "Smart contracts, DeFi protocols, and decentralized applications with enterprise-grade security.",
    capabilities: [
      "Smart Contract Development",
      "DeFi Protocols",
      "NFT Platforms",
      "Web3 Integration"
    ]
  },
  {
    id: 4,
    icon: Smartphone,
    title: "Mobile Engineering",
    description: "Native iOS and Android applications with pixel-perfect UI and optimized performance.",
    capabilities: [
      "Native Development",
      "Cross-platform Solutions",
      "Real-time Features",
      "Offline-first Architecture"
    ]
  },
  {
    id: 5,
    icon: Database,
    title: "Data Engineering",
    description: "Build robust data pipelines and analytics platforms that process millions of events per second.",
    capabilities: [
      "Real-time Streaming",
      "Data Warehousing",
      "ETL Pipelines",
      "Big Data Processing"
    ]
  },
  {
    id: 6,
    icon: Shield,
    title: "Cybersecurity",
    description: "Enterprise-grade security solutions to protect your digital assets and ensure compliance.",
    capabilities: [
      "Penetration Testing",
      "Security Audits",
      "Compliance Management",
      "Threat Detection"
    ]
  }
];

const ServicesPage = () => {
  const headerRef = useRef(null);
  const servicesRef = useRef(null);
  const processRef = useRef(null);

  useEffect(() => {
    // Animate header
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' }
      );
    }

    // Stagger service cards
    if (servicesRef.current) {
      const cards = servicesRef.current.querySelectorAll('.service-card');
      staggerCards(cards);
    }

    // Process steps animation
    if (processRef.current) {
      const steps = processRef.current.querySelectorAll('.process-step');
      fadeInOnScroll(steps, { stagger: 0.3 });
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="services-page pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-32">
        <div ref={headerRef} className="text-center">
          <span className="inline-block px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
            What We Do
          </span>
          <h1 className="font-headline text-5xl md:text-7xl font-bold tracking-headline uppercase mb-8">
            Technical <span className="text-[var(--primary)]">Capabilities</span>
          </h1>
          <p className="font-body text-[var(--on-surface-variant)] text-xl max-w-3xl mx-auto leading-relaxed">
            We specialize in building complex, mission-critical systems
            <br />
            that require precision engineering and architectural excellence
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={servicesRef} className="max-w-7xl mx-auto px-6 lg:px-8 mb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className="service-card group bg-[var(--surface-container-low)] p-10 hover:bg-[var(--surface-container-high)] transition-all duration-500 relative overflow-hidden"
              >
                {/* Top border reveal */}
                <div className="absolute top-0 left-0 w-full h-1 bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

                {/* Icon */}
                <div className="mb-8">
                  <Icon
                    size={56}
                    className="text-[var(--primary)] opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300"
                    strokeWidth={1.5}
                  />
                </div>

                {/* Title */}
                <h3 className="font-headline text-2xl font-bold tracking-tight uppercase mb-4 text-[var(--on-surface)]">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="font-body text-[var(--on-surface-variant)] leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Capabilities */}
                <ul className="space-y-2">
                  {service.capabilities.map((capability, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-3 font-body text-sm text-[var(--on-surface-variant)]"
                    >
                      <div className="w-1 h-1 bg-[var(--primary)] rounded-full flex-shrink-0" />
                      {capability}
                    </li>
                  ))}
                </ul>

                {/* Glow effect */}
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-[var(--primary)]/5 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            );
          })}
        </div>
      </section>

      {/* Our Process */}
      <section className="py-32 bg-[var(--surface-container-lowest)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="font-headline text-4xl md:text-6xl font-bold tracking-headline uppercase mb-6">
              Our <span className="text-[var(--primary)]">Process</span>
            </h2>
            <p className="font-body text-[var(--on-surface-variant)] text-lg max-w-2xl mx-auto">
              A proven methodology that delivers exceptional results
            </p>
          </div>

          <div ref={processRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '01', title: 'Discovery', description: 'Deep dive into your technical requirements and business objectives' },
              { number: '02', title: 'Architecture', description: 'Design scalable, future-proof system architecture' },
              { number: '03', title: 'Development', description: 'Agile development with continuous integration and testing' },
              { number: '04', title: 'Deployment', description: 'Launch, monitor, and optimize for peak performance' }
            ].map((step, index) => (
              <div
                key={index}
                className="process-step relative"
              >
                {/* Number */}
                <div className="font-headline text-6xl font-bold text-[var(--primary)]/20 mb-4">
                  {step.number}
                </div>

                {/* Title */}
                <h3 className="font-headline text-2xl font-bold uppercase mb-4">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="font-body text-[var(--on-surface-variant)] leading-relaxed">
                  {step.description}
                </p>

                {/* Connector line */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-12 -right-4 w-8 h-px bg-[var(--outline-variant)]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-headline text-4xl md:text-6xl font-bold tracking-headline uppercase mb-6">
              Tech <span className="text-[var(--primary)]">Stack</span>
            </h2>
            <p className="font-body text-[var(--on-surface-variant)] text-lg">
              Cutting-edge technologies we work with
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {[
              'React', 'Node.js', 'Python', 'Go', 'Rust', 'TypeScript',
              'AWS', 'Kubernetes', 'Docker', 'PostgreSQL', 'MongoDB', 'Redis',
              'TensorFlow', 'PyTorch', 'Solidity', 'Swift', 'Kotlin', 'GraphQL'
            ].map((tech, index) => (
              <div
                key={index}
                className="px-6 py-3 bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] border border-[var(--outline-variant)]/20 hover:border-[var(--primary)]/50 transition-all duration-300 group"
              >
                <span className="font-headline text-sm font-bold tracking-code uppercase text-[var(--on-surface-variant)] group-hover:text-[var(--primary)] transition-colors">
                  {tech}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-[var(--surface-container-lowest)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Sparkles size={48} className="text-[var(--primary)] mx-auto mb-8" />
          <h2 className="font-headline text-4xl md:text-5xl font-bold tracking-headline uppercase mb-8">
            Let's Build Something <span className="text-[var(--primary)]">Extraordinary</span>
          </h2>
          <p className="font-body text-[var(--on-surface-variant)] text-lg mb-12 leading-relaxed">
            We're currently accepting new projects for Q3-Q4 2025.
            <br />
            Tell us about your vision and let's make it a reality.
          </p>
          <Link
            to="/contact"
            className="magnetic inline-block bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-none"
          >
            Start a Project
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
